import { FieldValues } from 'react-hook-form'
import {
  addEntityData,
  editEntityData,
  editEntityDataEmbeddedAPI,
} from '../apis/entityData.api'
import { store } from './agent.store'
import { ENTITY_NAME, INTEGRATION_LOGS, NAMESPACES } from './constants'

export const updateInteractionStatus = async (
  communicationId: string,
  statusId: number
) => {
  try {
    await editEntityDataEmbeddedAPI(
      ENTITY_NAME.COMMUNICATION,
      communicationId as string,
      {
        id: communicationId,
        category_status: {
          id: statusId,
          _state: NAMESPACES.CHANGED,
        },
      },
      ['category_status']
    )
  } catch (err) {
    console.error(err)
  }
}

export const formatDuration = (seconds: number) => {
  const mins = String(Math.floor(seconds / 60)).padStart(2, '0')
  const secs = (seconds % 60).toFixed(0).padStart(2, '0')
  return `${mins}:${secs}`
}

export const isMultipleRecords = (data: FieldValues) => {
  const entityKeys = Object.keys(data)
  if (entityKeys?.length > 1) {
    return true
  }
  const entity = data[entityKeys[0]]
  if (!entity?.records || entity?.records?.length !== 1) {
    return true
  }
  if (entity?.records.length) {
    return true
  } else {
    return false
  }
}

const ctiLogIdByContact = new Map<string, string | number>()
const ctiLogCreateInFlight = new Set<string>()
const pendingCommunicationByContact = new Map<string, string | number>()

const getCtiEntityId = (): number | undefined => {
  const entityId = Number(
    store.getState()?.entity?.entitiesByName?.[INTEGRATION_LOGS.CTI_ENTITY_NAME]
      ?.id
  )
  return entityId || undefined
}

export const storeCtiLogIdForContact = (
  contactId: string,
  logId: string | number
): void => {
  if (!contactId || logId === undefined || logId === null) return
  ctiLogIdByContact.set(contactId, logId)
}

export const getCtiLogIdForContact = (
  contactId: string
): string | number | undefined => {
  if (!contactId) return undefined
  return ctiLogIdByContact.get(contactId)
}

export const clearCtiLogIdForContact = (contactId: string): void => {
  if (!contactId) return
  ctiLogIdByContact.delete(contactId)
  ctiLogCreateInFlight.delete(contactId)
  pendingCommunicationByContact.delete(contactId)
}

const linkCommunicationOnLog = async (
  logId: string | number,
  communicationId: string | number
): Promise<void> => {
  const entityId = getCtiEntityId()
  if (!entityId) return

  await editEntityData(
    INTEGRATION_LOGS.CTI_ENTITY_NAME,
    entityId,
    -1,
    {
      id: logId,
      communication_id: Number(communicationId) || communicationId,
    },
    logId
  )
}

export const createCtiLogOnIncoming = async (params: {
  contactId: string
  attributes?: Record<string, unknown>
}): Promise<string | number | undefined> => {
  const { contactId, attributes } = params
  if (!contactId) return undefined

  const existingLogId = getCtiLogIdForContact(contactId)
  if (existingLogId) return existingLogId
  if (ctiLogCreateInFlight.has(contactId)) return undefined

  const entityId = getCtiEntityId()
  if (!entityId) return undefined

  ctiLogCreateInFlight.add(contactId)
  try {
    const response = await addEntityData(
      INTEGRATION_LOGS.CTI_ENTITY_NAME,
      entityId,
      -1,
      {
        call_received_at: new Date().toISOString(),
        aws_contact_id: contactId,
        api_identifier: INTEGRATION_LOGS.CTI_API_IDENTIFIER,
        request_payload: { contactId },
        response_payload: attributes ?? {},
        record_status: 'ACTIVE',
      }
    )
    const logId = response?.data?.id
    if (logId !== undefined && logId !== null) {
      storeCtiLogIdForContact(contactId, logId)
      const pendingCommunicationId =
        pendingCommunicationByContact.get(contactId)
      if (pendingCommunicationId !== undefined) {
        pendingCommunicationByContact.delete(contactId)
        try {
          await linkCommunicationOnLog(logId, pendingCommunicationId)
        } catch (err) {
          console.error(err)
        }
      }
      return logId
    }
  } catch (err) {
    console.error(err)
  } finally {
    ctiLogCreateInFlight.delete(contactId)
  }
  return undefined
}

export const linkCtiLogCommunicationId = async (params: {
  contactId: string
  communicationId: string | number
}): Promise<void> => {
  const { contactId, communicationId } = params
  if (!contactId || communicationId === undefined || communicationId === null) {
    return
  }

  const logId = getCtiLogIdForContact(contactId)
  if (!logId) {
    pendingCommunicationByContact.set(contactId, communicationId)
    return
  }

  try {
    await linkCommunicationOnLog(logId, communicationId)
  } catch (err) {
    console.error(err)
  }
}
